<template>
  <div class="formula-formulate-adapter">
    <slot name="form" :fieldDefinitions="fieldDefinitions">
      <formulate-form :key="formulateKey" ref="form" v-model="input">
        <div class="flex gap-4 flex-col">
          <template v-for="(field) in fieldDefinitions">
            <formulate-field-adapter
              :disabled="viewOnly"
              :key="field.name"
              v-if="field.type !== 'group' && shouldShow(field.name)"
              @mounted="loadFieldRef"
              :value="field"
              :bindValidation="() => customValidation(field)"
            />
            <formulate-group-adapter
              @mounted-field="loadFieldRef"
              :disabled="viewOnly"
              :debug="debug"
              :key="field.name"
              v-else
              :value="field"
              :show-criteria="showCriteria"
            />
          </template>
        </div>
      </formulate-form>
    </slot>
  </div>
</template>

<script>


import { compileFields } from './compiler.ts'
import FormulateFieldAdapter from './FormulateFieldAdapter.vue'
import FormulateGroupAdapter from './FormulateGroupAdapter.vue'

export default {
  components: { FormulateFieldAdapter, FormulateGroupAdapter },
  props: {
    formDefinition: {
      type: Object,
      default: () => {}
    },
    value: {
      type: Object,
      default: () => {}
    },
    debug: {
      type: Boolean,
      default: false
    },
    viewOnly: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    fieldDefinitions: [],
    input: {},
    showCriteria: {},
    nullClears: {},
    debouncedUpdate: null,
    fieldRefs: {},
    fieldDefMap: {}
  }),

  mounted() {
    this.input = this.value
  },

  watch: {
    value(val) {
      this.input = val
    },
    /** Sem deep, mudanças em input.question_52 (mesmo objeto) não disparam — showCriteria não atualiza (ex.: I1 Sim não abre I2–I28). */
    input: {
      handler() {
        this.updateShow()
        this.updateValidations()
        if (this.debouncedUpdate) {
          this.debouncedUpdate(this.input)
        }
      },
      deep: true
    }
  },

  methods: {
    /** Ordem visual do formulário (grupos → filhos), para alinhar com registry.errors */
    orderedFieldNames() {
      const names = []
      for (const def of this.fieldDefinitions || []) {
        if (def.type === 'group' && def.children) {
          for (const child of def.children) {
            if (!child || !child.name) continue
            if (child.component && child.component !== 'h4') continue
            names.push(child.name)
          }
        } else if (def.name && def.type !== 'group') {
          names.push(def.name)
        }
      }
      return names
    },
    isFieldWrapperVisible(el) {
      let cur = el
      while (cur) {
        const style = window.getComputedStyle(cur)
        if (style.display === 'none' || style.visibility === 'hidden') return false
        cur = cur.parentElement
      }
      return true
    },
    fieldWrapperElement(fieldName) {
      const esc =
        typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
          ? CSS.escape(fieldName)
          : String(fieldName).replace(/\\/g, '\\\\').replace(/"/g, '\\"')
      return this.$el.querySelector(`[data-formula-field="${esc}"]`)
    },
    scrollToFirstInvalidField() {
      const form = this.$refs.form
      if (!form || !form.registry || !form.registry.errors) return

      const errors = form.registry.errors
      const ordered = this.orderedFieldNames()

      const pickElement = (preferVisible) => {
        for (const name of ordered) {
          if (!errors[name]) continue
          const el = this.fieldWrapperElement(name)
          if (!el) continue
          if (!preferVisible || this.isFieldWrapperVisible(el)) return el
        }
        return null
      }

      let el = pickElement(true)
      if (!el) el = pickElement(false)
      if (!el) return

      el.scrollIntoView({ behavior: 'smooth', block: 'center' })

      this.$nextTick(() => {
        const focusable = el.querySelector(
          'textarea, select, input:not([type="hidden"]):not([type="file"])'
        )
        if (focusable && typeof focusable.focus === 'function') {
          focusable.focus({ preventScroll: true })
        }
      })
    },
    update(val) {
      this.updateShow()
      val = this.applyNullClears(val)
      this.$emit('input', val)
    },
    shouldShow(id) {
      return this.showCriteria && this.showCriteria[id]
    },
    applyNullClears(val) {
      Object.keys(val || {}).forEach((fieldId) => {
        const value = val[fieldId]

        const valueIsNullLike = (
          value === 'no' || value === 'na' || !value
        )

        if (valueIsNullLike) {
          const clearFields = this._.get(this, `nullClears.${fieldId}`, null)
          if (clearFields && this._.isArray(clearFields) && clearFields.length > 0) {
            clearFields.forEach((fieldIdToClear) => {
              if (val[fieldIdToClear]) {
                const field = this.fieldRefs[fieldIdToClear]
                if (field) field.clear()
              }
              delete val[fieldIdToClear]
            })
          }
        }

      })

      return val
    },
    updateShow() {
      const input = this.input
      this.formDefinition.head.render.forEach((definition) => {

        let showWhen = ('show_when' in (definition.params || {}) ? definition.params.show_when : true)
        let showWhenValue = ('show_when_value' in (definition.params || {}) ? definition.params.show_when_value : false)

        if (this._.isString(showWhen)) {
          const parentElement = showWhen
          if(parentElement in input){
            if(showWhenValue){
              const inputValue = input[parentElement]
              const isMatch = Array.isArray(inputValue)
                            ? inputValue.includes(showWhenValue) // Se for array, verifica se contém o valor
                            : inputValue == showWhenValue; // Se for valor único, compara diretamente

              showWhen = isMatch
            }else{
              showWhen = (input[parentElement] !== 'no' && input[parentElement] !== 'na' && !!input[parentElement])
            }
          } else {
            showWhen = false
          }
        }

        const nullClear = this._.get(definition, 'params.null_clears', null)

        if (nullClear) {
          this.$set(this.$data.nullClears, definition.id, this._.isArray(nullClear) ? [...nullClear] : [nullClear])
        }

        this.$set(this.$data.showCriteria, definition.id, showWhen)
      })
    },
    makeFieldDefinitions(formDefinition) {
      return compileFields(formDefinition)
    },
    /** Achata todos os field-definitions (inclusive os filhos de grupos) em um mapa por name */
    buildFieldDefMap(definitions) {
      const map = {}
      const walk = (items) => {
        if (!Array.isArray(items)) return
        items.forEach((item) => {
          if (!item) return
          if (item.type === 'group' && Array.isArray(item.children)) {
            walk(item.children)
            return
          }
          if (item.name && item.render_params) {
            map[item.name] = item
          }
        })
      }
      walk(definitions)
      return map
    },
    /** Liga/desliga "required" dinamicamente conforme `required_when: { field, value }` no schema */
    updateValidations() {
      const input = this.input || {}
      const renderList = this._.get(this, 'formDefinition.head.render', [])
      renderList.forEach((definition) => {
        const params = definition.params || {}
        const requiredWhen = params.required_when
        if (!requiredWhen || !requiredWhen.field) return

        const fieldDef = this.fieldDefMap[definition.id]
        if (!fieldDef || !fieldDef.render_params) return

        const depField = requiredWhen.field
        const depValue = requiredWhen.value
        const currentValue = input[depField]
        const isActive = (typeof depValue !== 'undefined')
          ? currentValue === depValue
          : !!currentValue

        const baseValidation = String(params.validation || '')
        const baseTrim = baseValidation
          .split('|')
          .map(r => r.trim())
          .filter(r => r && r !== 'required')
          .join('|')

        const computed = isActive
          ? (baseTrim ? `required|${baseTrim}` : 'required')
          : baseTrim

        if (fieldDef.render_params.validation !== computed) {
          this.$set(fieldDef.render_params, 'validation', computed)
        }
      })
    },
    loadFieldRef(fieldName, fieldRef) {
      this.$set(this.$data.fieldRefs, fieldName, fieldRef)
    },
    customValidation(field) {
        console.log('bindValidation...[field]:', field)
        //const validation = this._.get(this.field.render_params, 'validation', null)
        //const showWhen = this._.get(this.field.render_params, 'show_when', null)
       // const showWhenValue = this._.get(this.field.render_params, 'show_when_value', null)

        // Se o campo não tem validação específica, retorna `true`
        if (!validation) return true;

        // Se há dependência (show_when), aplica validação condicional
        /*
        if (!showWhen && !showWhenValue) {
            return validation
        }else{
          return validation
        }
        */
    }, // End bindValidation
  },

  beforeMount() {
    this.fieldDefinitions = this.makeFieldDefinitions(this.formDefinition)
    this.fieldDefMap = this.buildFieldDefMap(this.fieldDefinitions)
    this.updateShow()
    this.updateValidations()
    this.debouncedUpdate = this._.debounce(
      this.update, 250
    )
  }
}
</script>

<style lang="scss">
.formula-formulate-adapter {
  @import "theme/snow/snow.scss";
  & .formulate-form h4 {
    padding-bottom: 0.5rem;
    padding-top: 0.5rem;
  }
}
</style>
